/* eslint-disable complexity */
import React, { useContext } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import {
  PREDICT_PRODUCT_SLUG,
  ROUTES,
  getForecastByIdOnboardingUrl,
  getForecastOnboardingUrl,
  getPredictPredictionsPageUrl,
  matchOnboardingForecastByIdRoute,
  matchOnboardingRoute,
  getConsolidatedForecastPredictionsUrl
} from '@futrli/shared-services';
import { Forecast } from '@futrli-sdk/predict';

import { useOnboardingUrlInfo } from '../../../onboarding/utils/hooks';
import { SCENARIO_WITH_INVOICES_ONBOARDING_STEP } from '../../../onboarding/const';
import { UserGridSettingsContext } from '../../reports/user-grid-settings/user-grid-settings-context';
import { TRACKING_EVENTS } from '../../../../core/constants';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { trackEvent } from '../../../../core/tracker';
import { useNavHeight } from '../../../../hooks/useNavHeight';
import { getAllNodesRefs } from '../util-fns/consolidation-util-fns';
import { OnboardingInvoicesNavigationTabs } from './onboarding-invoices-navigation-tabs';
import { OnboardingNavigationTabs } from './onboarding-navigation-tabs';
import { NavigationTabs } from './navigation-tabs';

const TOP_OFFSET = 50;

interface Props {
  disabled?: boolean;
  showInvoices?: boolean;
  showOnlyInvoices?: boolean;
}

export const ForecastingNavigationTabs = ({ disabled, showInvoices, showOnlyInvoices }: Props) => {
  const { pathname, search } = useLocation();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();

  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';

  const { navHeight } = useNavHeight();
  const topOffsetToUse = TOP_OFFSET;
  const topOffset = topOffsetToUse + navHeight;

  const {
    onboardingStep,
    isOnboardingScenarioWithInvoicesRoute,
    isOnboardingBudgetRoute,
    isOnboardingRoute,
    isOnboardingScenarioRoute
  } = useOnboardingUrlInfo();

  const navigate = useNavigate();

  const {
    forecast,
    showPredictionsExplanation,
    togglePredictionsExplanation,
    consolidatedForecastReport,
    forecastReportV2,
    isConsolidation
  } = usePredictionsInfoContext();

  const { clearExpandedRows, toggleExpandedRows } = useContext(UserGridSettingsContext);

  const isBudget = forecast?.kind === 'budget';
  const isBudgetDateSelect = pathname.indexOf(ROUTES.BUDGET_DATE_SELECT) !== -1;

  const isScenario = isConsolidation
    ? forecast?.kind === 'scenario'
    : forecast?.kind === 'scenario' && !(forecast as Forecast).include_unpaid_invoices;

  const handlePredictionExplanationsToggle = () => {
    trackEvent(TRACKING_EVENTS.CLICKED_EXPLAIN_PREDICTION);
    togglePredictionsExplanation?.();
    // If predictions explanation is turned on, clicking on switch will remove explanations and
    // we don't need to expand rows in that case
    if (showPredictionsExplanation) {
      return;
    }

    const forecastReport = isConsolidation ? consolidatedForecastReport : forecastReportV2;
    if (!forecastReport) {
      return;
    }

    clearExpandedRows();
    toggleExpandedRows(...getAllNodesRefs(forecastReport));
  };

  const handleTabClick = (route: string) => {
    if (
      isOnboardingScenarioWithInvoicesRoute &&
      onboardingStep === SCENARIO_WITH_INVOICES_ONBOARDING_STEP.INVOICES_BILLS_INCLUDED
    ) {
      return;
    }
    if (forecast) {
      if (isConsolidation) {
        navigate(
          `${getConsolidatedForecastPredictionsUrl(
            workspaceId,
            organisationId,
            PREDICT_PRODUCT_SLUG,
            forecast.id
          )}${route}${search}`
        );
      } else {
        navigate(`${getPredictPredictionsPageUrl(workspaceId, organisationId, forecast.id)}${route}${search}`);
      }
    }
  };

  const handleOnboardingTabClick = (route: string) => {
    if (disabled || showOnlyInvoices) {
      return;
    }

    if (matchOnboardingForecastByIdRoute(pathname) && forecast) {
      navigate(
        `${getForecastByIdOnboardingUrl(
          workspaceId,
          organisationId,
          forecast?.id,
          isOnboardingBudgetRoute,
          isOnboardingScenarioWithInvoicesRoute
        )}/${onboardingStep}${route}${location.search}`
      );
    } else if (matchOnboardingRoute(pathname)) {
      navigate(
        `${getForecastOnboardingUrl(
          workspaceId,
          organisationId,
          isOnboardingBudgetRoute,
          isOnboardingScenarioWithInvoicesRoute
        )}/${onboardingStep}${route}${location.search}`
      );
    }
  };

  if (showOnlyInvoices) {
    return <OnboardingInvoicesNavigationTabs handleTabClick={handleTabClick} topOffset={topOffset} />;
  }

  if (isOnboardingRoute) {
    return (
      <OnboardingNavigationTabs
        showInvoices={showInvoices}
        topOffset={topOffset}
        isOnboardingBudgetRoute={isOnboardingBudgetRoute}
        isOnboardingScenarioRoute={isOnboardingScenarioRoute}
        disabled={disabled}
        handleOnboardingTabClick={handleOnboardingTabClick}
        showPredictionsExplanation={showPredictionsExplanation}
        handlePredictionExplanationsToggle={handlePredictionExplanationsToggle}
        forecast={forecast}
        isConsolidation={isConsolidation}
      />
    );
  }

  return (
    <NavigationTabs
      topOffset={topOffset}
      isConsolidation={isConsolidation}
      isBudget={isBudget}
      isScenario={isScenario}
      isBudgetDateSelect={isBudgetDateSelect}
      showInvoices={showInvoices}
      handleTabClick={handleTabClick}
      showPredictionsExplanation={showPredictionsExplanation}
      handlePredictionExplanationsToggle={handlePredictionExplanationsToggle}
      forecast={forecast}
      disabled={disabled}
    />
  );
};
